import { Link } from 'react-router-dom';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, StatTile, Card, EmptyState } from '../../components/ui';
import { formatDateTime } from '../../lib/format';

interface AdminDash {
  season: { id: string; year: number; name: string } | null;
  activePlayers: number;
  currentWeek: {
    id: string;
    weekNumber: number;
    displayName: string;
    state: string;
    submissionsOpen: boolean;
    locksAt: string | null;
    submissions: number;
    gamesScheduled: number;
    eligibleGames: number;
    awaitingScores: number;
    hasTiebreaker: boolean;
    hasToken: boolean;
    resultsCalculated: boolean;
    lastSync: string | null;
    lastSyncError: string | null;
  } | null;
}

export function AdminDashboardPage() {
  const { data, error, loading } = useApi<AdminDash>('/admin/dashboard');
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;

  const cw = data.currentWeek;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl uppercase text-ink">Admin Dashboard</h1>
        <p className="text-sm text-muted">{data.season?.name ?? 'No season configured'}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Active players" value={data.activePlayers} />
        <StatTile label="Current week" value={cw ? `#${cw.weekNumber}` : '—'} sub={cw?.state} />
        <StatTile label="Submissions" value={cw?.submissions ?? 0} sub="this week" />
        <StatTile
          label="Awaiting scores"
          value={cw?.awaitingScores ?? 0}
          sub={`${cw?.eligibleGames ?? 0} eligible games`}
        />
      </div>

      {cw ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display text-3xl uppercase text-grove">{cw.displayName}</div>
              <div className="mt-1 flex flex-wrap gap-2 text-xs">
                <Flag ok={cw.hasToken} label="QR generated" />
                <Flag ok={cw.hasTiebreaker} label="SNF designated" />
                <Flag ok={cw.submissionsOpen} label="Submissions open" />
                <Flag ok={cw.resultsCalculated} label="Results calculated" />
              </div>
            </div>
            <Link to={`/admin/weeks/${cw.id}`} className="btn-primary">
              Manage this week →
            </Link>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Info label="Pick deadline" value={cw.locksAt ? formatDateTime(cw.locksAt) : 'Not set'} />
            <Info label="Games scheduled" value={String(cw.gamesScheduled)} />
            <Info label="Last score sync" value={cw.lastSync ? formatDateTime(cw.lastSync) : 'Never'} />
            <Info
              label="Sync status"
              value={cw.lastSyncError ? '⚠ Error' : 'OK'}
              danger={Boolean(cw.lastSyncError)}
            />
          </dl>
          {cw.lastSyncError && (
            <p className="mt-2 text-xs text-danger">Last sync error: {cw.lastSyncError}</p>
          )}
        </Card>
      ) : (
        <EmptyState title="No current week">
          Create a week to get started.{' '}
          <Link to="/admin/weeks" className="text-grove underline">
            Go to Weeks →
          </Link>
        </EmptyState>
      )}
    </div>
  );
}

function Flag({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`chip ${ok ? 'bg-success/15 text-success' : 'bg-line text-muted'}`}>
      {ok ? '✓' : '○'} {label}
    </span>
  );
}

function Info({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className={`font-medium ${danger ? 'text-danger' : ''}`}>{value}</dd>
    </div>
  );
}
